import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { startCheckout } from "@/pages/PaymentPages";
import { CGV_VERSION } from "@/content/cgv";

export default function AuthPage({ mode = "register" }) {
  const { t } = useLang();
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const nextFromUrl = sp.get("next"); // "scan" | "full" | "sub"
  const url = sp.get("url");
  // Pré-remplissage depuis `/payment/success` quand on arrive d'un Payment Link Stripe :
  // ?email=xxx&session_id=cs_live_...&prepaid=1
  const prefillEmail = sp.get("email") || "";
  const sessionId = sp.get("session_id");
  const prepaid = sp.get("prepaid") === "1";
  const [form, setForm] = useState({
    email: prefillEmail, password: "",
    first_name: "", last_name: "",
    company: "", vat_code: "",
  });
  const [loading, setLoading] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  // Sélecteur de package : "scan" (20 €) | "sub" (200 €) — obligatoire en register
  // sauf si un `next` valide est déjà passé en URL param, OU si on arrive d'un paiement
  // prépayé Stripe (le scan est déjà payé → pas de picker à afficher).
  const preselected = ["full", "sub", "scan"].includes(nextFromUrl) ? nextFromUrl : "";
  const [selectedPlan, setSelectedPlan] = useState(preselected);
  const showPlanPicker = mode === "register" && !preselected && !prepaid;
  const effectiveNext = preselected || (selectedPlan === "scan" ? "full" : selectedPlan);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !cgvAccepted) {
      toast.error(t("cgv.required"));
      return;
    }
    if (mode === "register" && showPlanPicker && !selectedPlan) {
      toast.error(t("auth.plan.required"));
      return;
    }
    setLoading(true);
    try {
      if (mode === "register") {
        await register({ ...form, cgv_accepted: true, cgv_version: CGV_VERSION });
        if (prepaid) {
          toast.success("Compte créé — votre scan prépayé est disponible");
          nav("/app");
          return;
        }
        toast.success("Compte créé");
      } else {
        await login(form.email, form.password);
        toast.success("Connecté");
      }
      // handle "next" actions — priorité au preselected (URL param) puis au choix user
      const routeNext = effectiveNext;
      if (routeNext === "full" && !prepaid) {
        toast.loading(t("pay.redirect"), { id: "full" });
        try { await startCheckout("scan_full", url || null); return; } catch (err) {
          toast.error(err?.response?.data?.detail || "Erreur", { id: "full" });
        }
      }
      if (routeNext === "sub") {
        toast.loading(t("pay.redirect"), { id: "sub" });
        try { await startCheckout("surveillance_monthly"); return; } catch (err) {
          toast.error(err?.response?.data?.detail || "Erreur", { id: "sub" });
        }
      }
      if (routeNext === "scan") { nav("/app/scan"); return; }
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="App min-h-screen brand-mesh">
      <Header variant="public" />
      <main className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-10 py-10 sm:py-14 md:py-20 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
        <div className="md:col-span-5">
          <img src="/brand/logo-icon.png" alt="" className="h-12 sm:h-14 w-auto mb-6 sm:mb-8" />
          <p className="eyebrow mb-3">{t("auth.eyebrow")}</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl tracking-tight mb-4 sm:mb-6">{mode === "register" ? t("auth.register") : t("auth.login")}</h1>
          <p className="text-muted-foreground leading-relaxed max-w-md text-sm sm:text-base">
            {mode === "register" ? t("auth.register.intro") : t("auth.login.intro")}
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {[t("auth.b1"), t("auth.b2"), t("auth.b3")].map((b) => (
              <li key={b} className="flex items-start gap-3">
                <span className="w-5 h-5 rounded-full bg-secondary/15 text-secondary flex items-center justify-center shrink-0 mt-0.5"><ShieldCheck className="w-3 h-3" /></span>
                <span className="text-foreground/80">{b}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-muted-foreground font-mono">{t("auth.demo_note")}</p>
        </div>

        <form onSubmit={submit} className="md:col-span-7 hairline p-5 sm:p-8 md:p-10 space-y-4 sm:space-y-5 shadow-[0_25px_50px_-25px_rgba(31,90,153,0.35)]" data-testid={mode === "register" ? "register-form" : "login-form"}>
          {prepaid && mode === "register" && (
            <div className="hairline p-4 rounded-lg bg-emerald-50 border-emerald-200" data-testid="prepaid-notice">
              <p className="font-serif text-base text-emerald-900 mb-1">Paiement validé — un scan prépayé vous attend</p>
              <p className="text-sm text-emerald-800/80">
                Nous avons bien reçu votre règlement{prefillEmail ? ` (${prefillEmail})` : ""}. Finalisez simplement la création de votre compte ci-dessous : votre crédit sera automatiquement attribué, aucun paiement supplémentaire.
              </p>
            </div>
          )}
          {showPlanPicker && (
            <div className="space-y-2" data-testid="plan-picker">
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("auth.plan.title")}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  { v: "scan", label: t("auth.plan.scan"), price: "20 €", sub: t("auth.plan.scan.sub"), testid: "plan-scan" },
                  { v: "sub", label: t("auth.plan.sub"), price: "200 € / mois", sub: t("auth.plan.sub.sub"), testid: "plan-sub" },
                ].map((p) => {
                  const active = selectedPlan === p.v;
                  return (
                    <button
                      type="button"
                      key={p.v}
                      onClick={() => setSelectedPlan(p.v)}
                      className={`text-left p-4 rounded-lg border-2 transition ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                      data-testid={p.testid}
                    >
                      <div className="flex items-baseline justify-between mb-1">
                        <p className="font-serif text-lg">{p.label}</p>
                        <p className="font-serif text-base text-primary font-bold">{p.price}</p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-snug">{p.sub}</p>
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-muted-foreground pt-1">{t("auth.plan.hint")}</p>
            </div>
          )}
          {mode === "register" && (
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("auth.first")} value={form.first_name} onChange={upd("first_name")} testid="input-first" required />
              <Field label={t("auth.last")} value={form.last_name} onChange={upd("last_name")} testid="input-last" required />
              <Field label={t("auth.company")} value={form.company} onChange={upd("company")} testid="input-company" />
              <Field label={t("auth.vat")} value={form.vat_code} onChange={upd("vat_code")} testid="input-vat" />
            </div>
          )}
          <Field label={t("auth.email")} type="email" value={form.email} onChange={upd("email")} testid="input-email" required />
          <Field label={t("auth.pwd")} type="password" value={form.password} onChange={upd("password")} testid="input-password" required minLength={mode === "register" ? 10 : 6} />
          {mode === "register" && (
            <p className="text-xs text-muted-foreground mt-1">Minimum 10 caractères.</p>
          )}
          {mode === "login" && (
            <p className="text-xs text-right mt-1"><Link to="/forgot-password" className="text-primary hover:underline" data-testid="auth-forgot-link">Mot de passe oublié ?</Link></p>
          )}

          {mode === "register" && (
            <label className="flex items-start gap-3 cursor-pointer select-none pt-1" data-testid="cgv-accept-label">
              <input type="checkbox" checked={cgvAccepted} onChange={(e) => setCgvAccepted(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-input accent-primary shrink-0" data-testid="cgv-accept-checkbox" />
              <span className="text-sm text-muted-foreground leading-relaxed">
                {t("cgv.accept.pre")}{" "}
                <Link to="/cgv" target="_blank" rel="noopener noreferrer" className="link-underline text-foreground font-medium" data-testid="cgv-link">{t("cgv.accept.link")}</Link>.
              </span>
            </label>
          )}

          <Button type="submit" disabled={loading || (mode === "register" && !cgvAccepted) || (showPlanPicker && !selectedPlan)} className="w-full h-12 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid="auth-submit">
            {loading ? "…" : (showPlanPicker && selectedPlan ? t("auth.submit_pay") : t("auth.submit"))} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-sm text-muted-foreground text-center pt-2">
            {mode === "register" ? (
              <>{t("auth.have")} <Link to={`/login${nextFromUrl ? `?next=${nextFromUrl}&url=${url||""}` : ""}`} className="link-underline text-foreground" data-testid="switch-login">{t("auth.login")}</Link></>
            ) : (
              <>{t("auth.no")} <Link to={`/register${nextFromUrl ? `?next=${nextFromUrl}&url=${url||""}` : ""}`} className="link-underline text-foreground" data-testid="switch-register">{t("auth.register")}</Link></>
            )}
          </p>
        </form>
      </main>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground mb-2 block">{label}</Label>
      <Input {...rest} className="h-12 rounded-lg border-input bg-white focus-visible:ring-2 focus-visible:ring-secondary/60 focus-visible:border-secondary" data-testid={testid} />
    </div>
  );
}
